import React, {forwardRef, useCallback, useImperativeHandle, useState} from "react";
import {Map} from "ol";
import Feature from "ol/Feature";
import VectorSource from "ol/source/Vector";
import {SelectEvent} from "ol/interaction/Select";
import {ZipcodePolygonButton} from "./ZipcodePolygonButton";
import {SectorPolygonButton} from "./SectorPolygonButton";
import {DrawPolygonButton} from "./DrawPolygonButton";
import {MarkerClusterButton} from "./MarkerClusterButton";
import {ToolButton} from "./TooltipButton";
import {Button} from "shared/ui";
import {DeleteSvg} from "shared/assets/svgs/DeleteSvg";
import {RefreshSvg} from "shared/assets/svgs/RefreshSvg";
import {IPlanLongItem, IShippingWithCluster} from "features/plan/types";
import {TShippingTooltip} from "../../../../shared/types";
import {useMapFunctions} from "../../../../shared/hooks/useMapFunctions";
import {PlanMapRef} from "./PlanMap";
import {GeolocationButton} from "./GeolocationButton";

type Props = {
  map?: Map;
  bound?: number[];
  zoom: number;
  selectedPlans: IPlanLongItem[];
  isVisibleTooltip?: boolean;
  onRefresh: () => void;
  onSelectShippings: (shippings: IShippingWithCluster[]) => void;
};

export const PlanMapControls = forwardRef<PlanMapRef, Props>(({
                                                                map,
                                                                bound,
                                                                zoom,
                                                                selectedPlans,
                                                                onRefresh,
                                                                onSelectShippings,
                                                              }: Props, ref) => {
  const [clusterSource, setClusterSource] = useState<VectorSource>();
  const [shippingTooltip, setShippingTooltip] = useState<TShippingTooltip<IShippingWithCluster>>();
  const {
          mouseOverCluster, clickCluster, resetSelectedShippings, addToSelectedShippings, getSelectedShippings,
        } = useMapFunctions<IShippingWithCluster>({onSelectShippings});
  
  useImperativeHandle<PlanMapRef, PlanMapRef>(ref, () => ({
    resetSelectedShippings
  }), [resetSelectedShippings]);
  
  const handleMouseOverCluster = useCallback((selected: Feature[], deselected: Feature[], event: SelectEvent) => {
    mouseOverCluster(selected, deselected, event, setShippingTooltip);
  }, [mouseOverCluster]);
  
  return (<>
    <div className={"float top left"}>
      <ZipcodePolygonButton map={map} bound={bound} zoom={zoom} />
      <SectorPolygonButton map={map} bound={bound} zoom={zoom} />
      <DrawPolygonButton
        map={map}
        clusterSource={clusterSource}
        onSelectShippings={onSelectShippings}
        getSelectedShippings={getSelectedShippings}
        addToSelectedShippings={addToSelectedShippings}
      />
      <MarkerClusterButton
        map={map}
        selectedPlans={selectedPlans}
        zoom={zoom}
        onInit={setClusterSource}
        onMouseOverCluster={handleMouseOverCluster}
        onClickCluster={clickCluster}
      />
      <ToolButton shippingTooltip={shippingTooltip} />
      <Button
        variant={"primary"}
        size={"sm"}
        className={"button false"}
        onClick={resetSelectedShippings}
      >
        <DeleteSvg color={"black"} />
      </Button>
      <Button
        variant={"primary"}
        size={"sm"}
        className={"button false"}
        onClick={onRefresh}
      >
        <RefreshSvg color={"black"} />
      </Button>
    </div>
    <div className={"float top right"}>
      <GeolocationButton map={map} />
    </div>
  </>);
});
